import React, { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';

export interface PhaseValues {
  realized: number;
  booked: number;
  pipeline: number;
}

type PhasedMetricsPanelVariant = 'icon-cards' | 'dot-cards' | 'summary-list';

export interface PhaseMetricConfig {
  key: string;
  label: string;
  description?: string;
  accentClass: string;
  icon?: React.ComponentType<{ className?: string }>;
  iconBgClass?: string;
  titleClass?: string;
  valueClass?: string;
  descriptionClass?: string;
  hoverBorderClass?: string;
  clickable?: boolean;
  onClick?: () => void;
}

interface PhasedMetricsPanelProps {
  title: string;
  subtitle?: string;
  metrics: PhaseMetricConfig[];
  summary: Record<string, PhaseValues | undefined>;
  variant?: PhasedMetricsPanelVariant;
  gridClassName?: string;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Math.trunc(amount));
};

const getMetricTooltipLines = (metric: PhaseMetricConfig): string[] => [
  metric.description ?? `${metric.label} across the phased economics model.`,
  'Realized: amounts already paid / collected.',
  'Booked: realized plus invoiced receivables expected to collect.',
  'Pipeline: booked plus placed orders not yet invoiced.'
];

export const PhasedMetricsPanel: React.FC<PhasedMetricsPanelProps> = ({
  title,
  subtitle,
  metrics,
  summary,
  variant = 'icon-cards',
  gridClassName
}) => {
  const [openTooltipKey, setOpenTooltipKey] = useState<string | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const visibleMetrics = metrics.filter(metric => summary[metric.key]);

  useEffect(() => {
    if (!openTooltipKey) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!panelRef.current?.contains(event.target as Node)) {
        setOpenTooltipKey(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [openTooltipKey]);

  if (visibleMetrics.length === 0) {
    return null;
  }

  const renderInfoButton = (metric: PhaseMetricConfig) => {
    const isOpen = openTooltipKey === metric.key;
    const tooltipId = `phased-tooltip-${metric.key}`;

    return (
      <div className="relative">
        <button
          type="button"
          aria-label={`Explain ${metric.label}`}
          aria-expanded={isOpen}
          aria-controls={tooltipId}
          className="inline-flex h-5 w-5 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-gray-600"
          onClick={(event) => {
            event.stopPropagation();
            setOpenTooltipKey(current => (current === metric.key ? null : metric.key));
          }}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <Info className="h-4 w-4" />
        </button>
        {isOpen && (
          <div
            id={tooltipId}
            role="dialog"
            aria-label={`${metric.label} details`}
            className="absolute left-0 top-7 z-20 w-72 rounded-xl border border-gray-200 bg-white p-4 text-left shadow-xl"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <p className="text-sm font-semibold text-gray-900">{metric.label}</p>
            <div className="mt-2 space-y-2 text-xs text-gray-600">
              {getMetricTooltipLines(metric).map(line => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (variant === 'summary-list') {
    return (
      <section ref={panelRef}>
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6">
          <h3 className="text-base lg:text-lg font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}

          <div className="mt-4 space-y-4">
            {visibleMetrics.map(metric => {
              const values = summary[metric.key];
              if (!values) {
                return null;
              }

              return (
                <div
                  key={metric.key}
                  className={`p-3 lg:p-4 bg-gray-50 rounded-xl ${
                    metric.clickable ? `cursor-pointer transition-colors ${metric.hoverBorderClass ?? 'hover:bg-gray-100'}` : ''
                  }`}
                  onClick={metric.onClick}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 ${metric.accentClass} rounded-full`}></div>
                      <span className="text-sm lg:text-base font-medium text-gray-700">{metric.label}</span>
                      {renderInfoButton(metric)}
                    </div>
                    <span className={`text-sm lg:text-base font-semibold ${metric.valueClass ?? 'text-gray-900'}`}>
                      {formatCurrency(values.realized)}
                    </span>
                  </div>
                  <div className="mt-2 pl-6 space-y-1 text-xs">
                    <div className="flex justify-between text-amber-600 font-medium">
                      <span>Booked</span>
                      <span>{formatCurrency(values.booked)}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>Pipeline</span>
                      <span>{formatCurrency(values.pipeline)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-4" ref={panelRef}>
      <div>
        <h3 className="text-base lg:text-lg font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}
      </div>

      <div className={gridClassName ?? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'}>
        {visibleMetrics.map(metric => {
          const values = summary[metric.key];
          if (!values) {
            return null;
          }

          if (variant === 'dot-cards') {
            return (
              <div
                key={metric.key}
                className={`bg-white shadow-sm rounded-2xl border border-gray-200 p-4 lg:p-6 ${
                  metric.clickable ? `cursor-pointer hover:shadow-md transition-all ${metric.hoverBorderClass ?? 'hover:border-gray-300'}` : ''
                }`}
                onClick={metric.onClick}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <h3 className={`text-sm lg:text-base font-semibold ${metric.titleClass ?? 'text-gray-900'}`}>{metric.label}</h3>
                    {renderInfoButton(metric)}
                  </div>
                  <div className={`w-3 h-3 ${metric.accentClass} rounded-full`}></div>
                </div>
                <p className={`text-xl lg:text-2xl font-bold ${metric.valueClass ?? 'text-gray-900'} mb-1`}>
                  {formatCurrency(values.realized)}
                </p>
                {metric.description && (
                  <p className={`text-xs lg:text-sm ${metric.descriptionClass ?? 'text-gray-600'}`}>
                    {metric.description}
                  </p>
                )}
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-amber-600 font-medium">
                    Booked: {formatCurrency(values.booked)}
                  </p>
                  <p className="text-xs text-gray-400">
                    Pipeline: {formatCurrency(values.pipeline)}
                  </p>
                </div>
              </div>
            );
          }

          const Icon = metric.icon;
          if (!Icon || !metric.iconBgClass) {
            return null;
          }

          return (
            <div
              key={metric.key}
              className={`bg-white shadow-sm rounded-2xl border border-gray-200 p-6 ${
                metric.clickable ? `cursor-pointer hover:shadow-md transition-all ${metric.hoverBorderClass ?? 'hover:border-gray-300'}` : ''
              }`}
              onClick={metric.onClick}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-600">{metric.label}</p>
                    {renderInfoButton(metric)}
                  </div>
                  <p className={`text-2xl font-bold ${metric.valueClass ?? 'text-gray-900'}`}>
                    {formatCurrency(values.realized)}
                  </p>
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-amber-600 font-medium">
                      Booked: {formatCurrency(values.booked)}
                    </p>
                    <p className="text-xs text-gray-400">
                      Pipeline: {formatCurrency(values.pipeline)}
                    </p>
                  </div>
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${metric.iconBgClass}`}>
                  <Icon className={`h-6 w-6 ${metric.accentClass}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
